<template>
  <v-menu v-model="isOpen" class="select-menu" width="400px" max-height="550px" location="end" target="parent"
    scroll-strategy="block" :close-on-content-click="false" @after-leave="closeMenu">
    <rb-loader v-model="isLoading" :contained="true" :generic="true" />
    <v-window v-model="windowIndex" class="elevation-2">
      <TabWindowMenu v-model:reset="resetMenu" :data="contextsList" :type="menuConfig.context.type"
        :titulo="menuConfig.context.titulo" :not-found="menuConfig.context.notFound"
        :condition="menuConfig.context.condition" :index="0" class="context-menu-window-item" :searchType="'simple'"
        @menu-select="selectContext" @menu-close="closeMenu" />

      <TabWindowMenu v-model:reset="resetMenu" :data="availableEntities" :type="menuConfig.entity.type"
        :titulo="menuConfig.entity.titulo" :not-found="menuConfig.entity.notFound"
        :condition="menuConfig.entity.condition" :index="1" class="context-menu-window-item" :searchType="'simple'"
        @menu-return="goBack" @menu-select="selectEntity" @menu-close="closeMenu" />

      <TabWindowMenu v-model="valuesSelection" v-model:reset="resetMenu" v-model:state-control="checkboxState"
        :data="displayedItems" :type="menuConfig.values.type" :titulo="menuConfig.values.titulo"
        :not-found="menuConfig.values.notFound" :condition="menuConfig.values.condition" :index="2"
        class="context-menu-window-item" :searchType="'complex'" @menu-return="goBack" @menu-close="closeMenu"
        @menu-save="saveSelections" @scrolled-itens="handleScroll" @search="handleSearch" :condition-value="externalCondition"/>
    </v-window>
  </v-menu>
</template>

<script setup>
import { defineProps, defineEmits, reactive, ref, computed, watch, onMounted, shallowRef } from 'vue';
import { fetchContextos, contextData } from '@/ai_studio/utils/fetchData';
import TabWindowMenu from './TabWindowMenu';
import RbLoader from '@/ai_studio/components/common/RbLoader.vue';

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  data: { type: Array, required: true },
  updateData: { type: Boolean, default: false },
  loadingStatus: { type: Boolean, default: false },
  selectedContext: {type: Object, default: ()=>({})},
  selectedEntity: {type: Object, default: ()=>({})},
  selectedCondition: {type: Object, default: ()=>({})},
  insightsFilter: { type: Boolean, default: false },
});

const emit = defineEmits(['update:data', 'update:updateData', 'update:modelValue', 'update:loadingStatus', 'clearSelectionProps']);
const isOpen = ref(false);
const windowIndex = ref(0);
const contextsList = shallowRef([]);
const filters = ref([]);
const isLoading = ref(false);
const resetMenu = ref(true);
const selectedContext = ref({});
const selectedEntity = ref({});
const checkboxState = ref(false);
const displayedItems = shallowRef([]);
const lastSearchQuery = ref('');
const currentPage = ref(1);
const itemsPerPage = ref(50);
const entityCache = { items: {} };

const menuConfig = reactive({
  context: {
    titulo: 'Escolha um contexto',
    notFound: 'Nenhum contexto encontrado',
    search: true,
    type: 'list-item',
    condition: false,
  },
  entity: {
    titulo: 'Escolha uma opção',
    notFound: 'Nenhuma opção encontrada',
    search: true,
    type: 'list-item',
    condition: false,
  },
  values: {
    titulo: 'Escolha um item',
    notFound: 'Nenhum item encontrado',
    search: true,
    type: 'checkbox',
    condition: true,
  },
});

const apiEndpoints = {
  categoria: 'assistents/crm/listarModalidades',
  item: 'assistents/crm/listarCursos',
  local: 'assistents/crm/listarLocais',
  nivel: 'assistents/crm/listarNiveisEnsino',
  processo: 'assistents/crm/listarProcessosSeletivos',
  subitem: 'assistents/crm/listarOfertas',
};

const externalCondition = ref(null);
const getGroupSelectionIds = (selection) => {
  if (!selection) return [];
  if (selection.todos) return 'all';
  
  return selection._selectedIds?.length > 0
    ? selection._selectedIds
    : selection.dados?.map(d => d.id) || [];
};

const getSelectedIds = () => {
  if (!selectedEntity.value?.selected) return new Set();
  
  const selectedIds = new Set();
  const selectionGroups = ['Contém', 'Não contém'];
  
  for (const groupType of selectionGroups) {
    const selection = selectedEntity.value.selected[groupType];
    const ids = getGroupSelectionIds(selection);

    if (ids === 'all') return 'all';
    ids.forEach(id => selectedIds.add(id));
  }
  return selectedIds;
};

const sortItemsBySelection = (items) => {
  const selectedIds = getSelectedIds();
  if (selectedIds === 'all') return items;
  
  return [...items].sort((a, b) => {
    const aSelected = selectedIds.has(a.id);
    const bSelected = selectedIds.has(b.id);

    if (aSelected && !bSelected) return -1;
    if (!aSelected && bSelected) return 1;
    return 0;
  });
};

const getItemSearchText = (item) => {
  const name = item?.titulo || item?.nome || '';
  const codigo = item.codigo || '';
  return {
    name: name.toLowerCase(),
    codigo: codigo.toLowerCase(),
    full: `${codigo ? codigo + ' - ' : ''}${name}`.toLowerCase()
  };
};

const getFilteredItems = (items, query) => {
  if (!query) return items;
  
  const lowerQuery = query.toLowerCase();
  return items.filter(item => {
    const searchText = getItemSearchText(item);
    return searchText.name.includes(lowerQuery) || 
           searchText.codigo.includes(lowerQuery) || 
           searchText.full.includes(lowerQuery);
  });
};

const handleSearch = async (query) => {
  if (!selectedEntity.value?.id) return;

  const cacheKey = selectedEntity.value.id;
  lastSearchQuery.value = query;
  currentPage.value = 1;

  let results = entityCache.items[cacheKey];
  let filteredResult = [];

  query ? filteredResult = getFilteredItems(results, query) : filteredResult = results || [];
  
  const sortedResults = sortItemsBySelection(filteredResult);
  displayedItems.value = markSelectedItems(sortedResults.slice(0, itemsPerPage.value));
};

const availableEntities = computed(() => {
  const entidades = selectedContext.value.entidades || [];
  if (props.insightsFilter === true) {
    const indexesToRemove = [1, 4, 5];
    const entidadesInsight = entidades.filter((item, index) => !indexesToRemove.includes(index));
    return [...entidadesInsight, { id: 2, nome: "Faixa de conversão" }];
  }
  
  return entidades;
});
const valuesSelection = computed(() => selectedEntity.value.selected || {});

const createEmptySelection = () => ({
  'Contém': { dados: [], todos: false, codigo: 'CONTEM', _selectedIds: [] },
  'Não contém': { dados: [], todos: false, codigo: 'NAO_CONTEM', _selectedIds: [] },
  'Não está vazio': { state: false, todos: false, codigo: 'NAO_VAZIO' },
  'Vazio': { state: false, todos: false, codigo: 'VAZIO' },
});

const checkForAllSelected = (selectedEntity) => {
  for (const groupType of ['Contém', 'Não contém']) {
    if (selectedEntity.selected[groupType]?.todos) return { isAll: true, groupType };
  }
  return { isAll: false };
};

const markAllItems = (items, groupType) => {
  return items.map(item => ({
    ...item,
    _selected: true,
    _selectedIn: groupType
  }));
};

const buildSelectionMap = (selectedEntity) => {
  const selectedMap = new Map();
  for (const groupType of ['Contém', 'Não contém']) {
    const selection = selectedEntity.selected[groupType];
    if (!selection) continue;
    
    const selectedIds = selection._selectedIds?.length > 0
      ? selection._selectedIds
      : selection.dados?.map(d => d.id) || [];

    selectedIds.forEach(id => selectedMap.set(id, groupType));
  }
  
  return selectedMap;
};

const markSelectedItems = (items) => {
  if (!selectedEntity.value?.selected) return items;
  const result = [...items];

  const { isAll, groupType } = checkForAllSelected(selectedEntity.value);
  
  if (isAll) return markAllItems(result, groupType);
  
  const selectedMap = buildSelectionMap(selectedEntity.value);
  
  return result.map(item => ({
    ...item,
    _selected: !!selectedMap.get(item.id),
    _selectedIn: selectedMap.get(item.id) || null
  }));
};

const processContexts = (contexts) => {
  if (!Array.isArray(contexts)) return;

  contextsList.value = contexts.map(ctx => ({
    id: ctx.id,
    titulo: ctx.titulo,
    entidades: [
      { ...ctx.entidadeCategoria, tipoEntidade: 'categoria', itens: [], selected: createEmptySelection() },
      { ...ctx.entidadeItem, tipoEntidade: 'item', itens: [], selected: createEmptySelection() },
      { ...ctx.entidadeLocal, tipoEntidade: 'local', itens: [], selected: createEmptySelection() },
      { ...ctx.entidadeNivel, tipoEntidade: 'nivel', itens: [], selected: createEmptySelection() },
      { ...ctx.entidadeProcesso, tipoEntidade: 'processo', itens: [], selected: createEmptySelection() },
      { ...ctx.entidadeSubItem, tipoEntidade: 'subitem', itens: [], selected: createEmptySelection() }
    ]
  }));
};

const selectContext = (context) => {
  selectedContext.value = context;
  windowIndex.value++;
};

const updateMenuConfig = (entity) => {
  menuConfig.values.titulo = entity.nome;
  menuConfig.values.notFound = `Nenhum ${entity.nome.toLowerCase()} encontrado`;
};

const selectEntity = async (entity) => {
  updateMenuConfig(entity);
  entity.itens = [];
  displayedItems.value = [];
  lastSearchQuery.value = '';
  await fetchItems(apiEndpoints[entity.tipoEntidade], entity);
};

const fetchEntityItems = async (endpoint) => {
  const response = await fetch(`/api/${endpoint}?contexto=${selectedContext.value.id}`);
  const data = await response.json();

  if (!data.data) return null;
  return data.data;
};

const faixaConversaoItens = [
  {
    id: 1,
    titulo: "Menor que 60",
    _selected: false,
    _selectedInd: null
  },
  {
    id: 2,
    titulo: "Entre 60 e 70",
    _selected: false,
    _selectedInd: null
  },
  {
    id: 3,
    titulo: "Entre 70 e 90",
    _selected: false,
    _selectedInd: null
  },
  {
    id: 4,
    titulo: "Maior que 90",
    _selected: false,
    _selectedInd: null
  },
]
const fetchItems = async (endpoint, entity) => {
  try {
    const cacheKey = entity.id;
    isLoading.value = true;
    if(endpoint == null || endpoint == undefined) {
      const sortedItems = sortItemsBySelection(faixaConversaoItens);
      displayedItems.value = markSelectedItems(sortedItems);
      checkboxState.value = true;
      return windowIndex.value++;
    }
    if (!entityCache.items[cacheKey]) {
      const items = await fetchEntityItems(endpoint, entity.id);
      if (!items) return windowIndex.value++;
      entityCache.items[cacheKey] = items;
    }

    selectedEntity.value = entity;
    currentPage.value = 1;
    const sortedItems = sortItemsBySelection(entityCache.items[cacheKey]);
    const initialItems = sortedItems.slice(0, itemsPerPage.value);
    displayedItems.value = markSelectedItems(initialItems);
    
    checkboxState.value = true;
    windowIndex.value++;
  } catch (err) {
    console.error(err);
  } finally {
    isLoading.value = false;
  }
};

const hasMoreItems = (items, startIndex) => {
  return startIndex < items.length;
};

const getNextPageItems = (items, startIndex, pageSize) => {
  if (!hasMoreItems(items, startIndex)) return [];
  
  return items.slice(startIndex, startIndex + pageSize);
};

const loadMoreItems = async () => {
  if (!selectedEntity.value?.id) return;
  
  const cacheKey = selectedEntity.value.id;
  const cachedItems = entityCache.items[cacheKey];
  if (!cachedItems) return;
  
  const startIndex = currentPage.value * itemsPerPage.value;
  
  const filteredItems = getFilteredItems(cachedItems, lastSearchQuery.value);
  const sortedItems = sortItemsBySelection(filteredItems);
  
  const nextItems = getNextPageItems(sortedItems, startIndex, itemsPerPage.value);
  if (nextItems.length === 0) return;
  
  currentPage.value++;
  const processedItems = markSelectedItems(nextItems);
  displayedItems.value = [...displayedItems.value, ...processedItems];
};

const goBack = () => windowIndex.value--;

const resetMenuState = () => {
  isOpen.value = false;
  windowIndex.value = 0;
  resetMenu.value = true;
  currentPage.value = 1;
  displayedItems.value = [];
  selectedEntity.value = {};
  externalCondition.value = null;
  selectedContext.value = {};
};

const closeMenu = () => {
  resetMenuState();
  emit('clearSelectionProps');
};

const saveSelections = (selections) => {
  processSelectionGroup(selections, 'Contém');
  processSelectionGroup(selections, 'Não contém');
  selectedEntity.value.selected = JSON.parse(JSON.stringify(selections));
  updateFilters();
  closeMenu();
};

const processSelectionGroup = (selections, groupName) => {
  if (!selections[groupName]) return;

  if (selections[groupName].todos) {
    selections[groupName].dados = [];
    selections[groupName]._selectedIds = [];
    return;
  }

  if (selections[groupName].dados) {
    selections[groupName]._selectedIds = selections[groupName].dados.map(item => item.id);
  }
};

const isConditionSelected = condition =>
  condition.todos === true || (condition.dados?.length > 0) || condition.state === true;

const hasSelectedConditions = selected =>
  Object.values(selected).some(condition => isConditionSelected(condition));

const updateFilters = () => {
  filters.value = contextsList.value
    .map(context => {
      const filteredEntities = context.entidades
        .filter(entity => hasSelectedConditions(entity.selected))
        .map(entity => ({
          entidadeId: entity.id,
          entidadeTipoId: entity.tipoId,
          entidadeTitulo: entity.nome,
          dados: Object.entries(entity.selected)
            //eslint-disable-next-line no-unused-vars
            .filter(([_, condition]) => isConditionSelected(condition))
            .map(([key, condition]) => ({
              titulo: key,
              codigo: condition.codigo,
              dados: condition.todos ? [] : (condition.dados || []),
              todos: condition.todos || false,
              ...(condition.state !== undefined ? { state: true } : {})
            }))
        }))
        .filter(entity => entity.dados.length > 0);

      return {
        contextoId: context.id,
        contextoTitulo: context.titulo,
        entidades: filteredEntities
      };
    })
    .filter(context => context.entidades.length > 0);

  emit('update:data', filters.value);
};

const syncState = async () => {
  return contextsList.value.map(context => {
    const selectedContext = filters.value.find(sc => sc.contextoId === context.id);
    if (!selectedContext) {
      return {
        ...context,
        entidades: context.entidades.map(entity => ({
          ...entity,
          selected: createEmptySelection()
        }))
      };
    }

    return {
      ...context,
      entidades: context.entidades.map(entity => {
        const selectedEntity = selectedContext.entidades.find(se => se.entidadeId === entity.id);
        
        if (!selectedEntity) {
          return { ...entity, selected: createEmptySelection() };
        }

        const newSelected = createEmptySelection();
        selectedEntity.dados.forEach(condition => applyCondition(newSelected, condition));
        return { ...entity, selected: newSelected };
      })
    };
  });
};

const findReferences = async (filterContext, filterEntity, filterCondition) => {
  if (!filterContext?.contextoId || !filterEntity?.entidadeId) return;
  
  const foundContext = contextsList.value.find(ctx => ctx.id === filterContext.contextoId);
  if (!foundContext) return;

  selectedContext.value = foundContext;
  windowIndex.value = 1;

  const foundEntity = foundContext.entidades.find(entity => entity.id === filterEntity.entidadeId);
  if (!foundEntity) return;

  await selectEntity(foundEntity);
  externalCondition.value = filterCondition?.titulo || null;
  windowIndex.value = 2;
}

const applyCondition = (selectedObj, condition) => {
  const title = condition.titulo;

  if (title === 'Contém' || title === 'Não contém') {
    selectedObj[title].todos = condition.todos || false;
    if (!condition.todos && condition.dados?.length) {
      selectedObj[title].dados = [...condition.dados];
      selectedObj[title]._selectedIds = condition.dados.map(item => item.id);
    }
    return;
  }
  if (title === 'Não está vazio' || title === 'Vazio') {
    selectedObj[title].state = true;
    return;
  }
};

const handleScroll = (event) => {
  const target = event.target;
  const scrollLimit = 255;
  const scrollPosition = target.scrollHeight - target.scrollTop - target.clientHeight;
  if (scrollPosition < scrollLimit) loadMoreItems();
};

watch(() => props.updateData, async (newVal) => {
  if (!newVal) return;
  
  contextsList.value = await syncState();
  emit('update:updateData', false);
});

watch(() => props.data, (newVal) => {
  filters.value = newVal;
});

watch(() => props.modelValue, async (state) => {
  if (!state) return;
  try {
    emit('update:loadingStatus', true);

    if (!contextsList.value.length) {
      let response = contextData.value || await fetchContextos();
      if(props.insightsFilter === true) {
        response = [response[0]]
      }
      processContexts(response);
    }

    contextsList.value = await syncState();
    if(props.selectedContext && props.selectedEntity && props.selectedCondition) findReferences(props.selectedContext, props.selectedEntity, props.selectedCondition);
    isOpen.value = state;
  } catch (err) {
    console.error(err);
    isOpen.value = false;
  } finally {
    emit('update:loadingStatus', false);
    emit('update:modelValue', false);
  }
});

onMounted(() => {
  filters.value = props.data;
});
</script>

<style scoped>
.select-menu:deep(.v-window__container) {
  margin-top: 0 !important;
  height: 100%;
}
</style>
